import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import mixpanel, { OverridedMixpanel } from "mixpanel-browser";
import { env } from "@config";
import { useAppSelector } from "@store";
import { MixpanelContextValue } from "./mixpanel.types";

export interface MixpanelProviderProps {
  children: React.ReactNode;
}

export const MixpanelContext = createContext<MixpanelContextValue>({
  trackEvent: () => {},
});

export const MixpanelProvider: React.FC<MixpanelProviderProps> = ({ children }) => {
  const user = useAppSelector((state) => state.auth.user);

  const [isInitialized, setIsInitialized] = useState(false);
  const mixpanelInstance = useRef<OverridedMixpanel>(mixpanel);

  const trackEvent = useCallback(
    (event: string) => {
      if (isInitialized) {
        mixpanelInstance.current.track(`outlookAddIn.${event}`, {});
      }
    },
    [isInitialized]
  );

  const mixpanelContextValue = useMemo(
    () => ({
      trackEvent,
    }),
    [trackEvent]
  );

  useEffect(() => {
    if (!mixpanelInstance.current.people) {
      mixpanelInstance.current.init(env.mixpanelId, {
        debug: env.mode !== "prod",
        ignore_dnt: true,
        ip: false,
      });
    }

    if (user) {
      mixpanelInstance.current.identify(`${user.orgId}|${user.userId}`);
      mixpanelInstance.current.people.set_once({
        name: user.name,
        firstName: user.firstName,
        lastName: user.lastName,
        orgId: user.orgId,
        userId: user.userId,
      });
      setIsInitialized(true);
    } else {
      mixpanelInstance.current.reset();
      setIsInitialized(false);
    }
  }, [user]);

  return (
    <MixpanelContext.Provider value={mixpanelContextValue}>{children}</MixpanelContext.Provider>
  );
};

export const useMixpanel = (): MixpanelContextValue => useContext(MixpanelContext);
